/**
 * MODAIRA Cart Drawer & AJAX Cart Engine
 * Handles slide-in drawer interactions, quantity adjustments, line removal,
 * 1-click upsell additions, discount application, and header counter synchronization.
 */

(function() {
  class ModairaCart {
    constructor() {
      this.drawer = document.getElementById('ModairaCartDrawer');
      this.backdrop = document.getElementById('ModairaCartBackdrop');
      this.closeBtn = document.getElementById('ModairaCartClose');
      this.itemsContainer = document.getElementById('ModairaCartItems');
      this.subtotalElement = document.getElementById('ModairaCartSubtotal');
      this.emptyState = document.getElementById('ModairaCartEmpty');
      this.footerSection = document.getElementById('ModairaCartFooter');
      this.headerCounters = document.querySelectorAll('.cart-count-badge');
      this.discountInput = document.getElementById('ModairaCartDiscountInput');
      this.discountBtn = document.getElementById('ModairaCartApplyDiscount');
      this.checkoutBtn = document.getElementById('ModairaCartCheckoutBtn');
      this.appliedDiscount = '';

      this.initEvents();
    }

    initEvents() {
      // Trigger buttons throughout the store
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-drawer-trigger], .header-cart-btn, a[href="/cart"]');
        // Only intercept if not on the actual /cart page or if specifically requested
        if (trigger && !trigger.hasAttribute('data-no-drawer')) {
          // If on /cart page and user clicked "View Cart" inside drawer, allow normal navigation
          if (trigger.getAttribute('id') === 'ModairaViewCartLink') {
            return;
          }
          // If clicking header cart on a regular page, open drawer instead of page jump
          if (window.location.pathname !== '/cart' || trigger.hasAttribute('data-cart-drawer-trigger')) {
            e.preventDefault();
            this.open();
          }
        }

        // Close drawer triggers
        if (e.target.closest('#ModairaCartClose') || e.target.closest('#ModairaCartBackdrop')) {
          e.preventDefault();
          this.close();
        }

        // Quantity Stepper Minus
        const minusBtn = e.target.closest('.js-drawer-qty-minus');
        if (minusBtn) {
          e.preventDefault();
          const key = minusBtn.getAttribute('data-line-key');
          const currentQty = parseInt(minusBtn.getAttribute('data-qty'), 10) || 1;
          this.updateQuantity(key, currentQty - 1);
        }

        // Quantity Stepper Plus
        const plusBtn = e.target.closest('.js-drawer-qty-plus');
        if (plusBtn) {
          e.preventDefault();
          const key = plusBtn.getAttribute('data-line-key');
          const currentQty = parseInt(plusBtn.getAttribute('data-qty'), 10) || 1;
          this.updateQuantity(key, currentQty + 1);
        }

        // Line Item Remove Button
        const removeBtn = e.target.closest('.js-drawer-item-remove');
        if (removeBtn) {
          e.preventDefault();
          const key = removeBtn.getAttribute('data-line-key');
          this.updateQuantity(key, 0);
        }

        // 1-Click Upsell Add Button
        const upsellBtn = e.target.closest('.js-drawer-upsell-add');
        if (upsellBtn) {
          e.preventDefault();
          const variantId = upsellBtn.getAttribute('data-variant-id');
          if (variantId) {
            upsellBtn.disabled = true;
            upsellBtn.textContent = 'Adding...';
            this.addItem(variantId, 1, () => {
              upsellBtn.disabled = false;
              upsellBtn.textContent = 'Added ✓';
              setTimeout(() => {
                upsellBtn.textContent = '+ Add';
              }, 2000);
            });
          }
        }
      });

      // Escape key listener
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Discount Code Form Hook
      if (this.discountBtn && this.discountInput) {
        this.discountBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const code = this.discountInput.value.trim();
          if (code) {
            this.appliedDiscount = code;
            this.updateCheckoutUrlWithDiscount(code);
            this.discountBtn.textContent = 'Applied ✓';
            setTimeout(() => {
              this.discountBtn.textContent = 'Apply';
            }, 2500);
          }
        });
      }

      // Intercept standard Add to Cart forms across the site
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action*="/cart/add"], form.product-form');
        if (form && !form.hasAttribute('data-no-ajax')) {
          e.preventDefault();
          const submitBtn = form.querySelector('[type="submit"][name="add"], .atc-btn');
          const originalText = submitBtn ? submitBtn.innerHTML : '';
          if (submitBtn) {
            submitBtn.disabled = true;
          }

          const formData = new FormData(form);

          fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              'Accept': 'application/json'
            }
          })
          .then(res => res.json())
          .then(item => {
            if (item.status && item.status !== 200) {
              alert(item.description || item.message || 'Error adding item to cart.');
            } else {
              this.fetchAndUpdateCart(() => {
                this.open();
              });
            }
          })
          .catch(err => {
            console.error('Error adding product to cart:', err);
          })
          .finally(() => {
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.innerHTML = originalText;
            }
          });
        }
      });
    }

    isOpen() {
      return this.drawer && this.drawer.classList.contains('modaira-cart-drawer--open');
    }

    open() {
      this.fetchAndUpdateCart();
      if (this.drawer) this.drawer.classList.add('modaira-cart-drawer--open');
      if (this.backdrop) this.backdrop.classList.add('modaira-cart-backdrop--active');
      document.body.classList.add('modaira-cart-lock-scroll');
    }

    close() {
      if (this.drawer) this.drawer.classList.remove('modaira-cart-drawer--open');
      if (this.backdrop) this.backdrop.classList.remove('modaira-cart-backdrop--active');
      document.body.classList.remove('modaira-cart-lock-scroll');
    }

    addItem(variantId, quantity = 1, callback) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      })
      .then(res => res.json())
      .then(data => {
        if (data.status && data.status !== 200) {
          alert(data.description || data.message || 'Error adding item.');
        } else {
          this.fetchAndUpdateCart();
          if (callback) callback(data);
        }
      })
      .catch(err => {
        console.error('Error adding item to cart:', err);
      });
    }

    updateQuantity(lineKey, quantity) {
      if (this.drawer) this.drawer.classList.add('modaira-cart-drawer--loading');

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity
        })
      })
      .then(res => res.json())
      .then(cart => {
        this.renderCartData(cart);
      })
      .catch(err => {
        console.error('Error updating cart line quantity:', err);
      })
      .finally(() => {
        if (this.drawer) this.drawer.classList.remove('modaira-cart-drawer--loading');
      });
    }

    fetchAndUpdateCart(callback) {
      fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      })
      .then(res => res.json())
      .then(cart => {
        this.renderCartData(cart);
        if (callback) callback(cart);
      })
      .catch(err => console.error('Error fetching cart:', err));
    }

    renderCartData(cart) {
      // 1. Update Header Badges
      this.headerCounters = document.querySelectorAll('.cart-count-badge');
      this.headerCounters.forEach(badge => {
        badge.textContent = cart.item_count;
        if (cart.item_count === 0) {
          badge.classList.add('cart-count-badge--empty');
        } else {
          badge.classList.remove('cart-count-badge--empty');
        }
      });

      // 2. Toggle Empty State vs Items List
      if (cart.item_count === 0) {
        if (this.itemsContainer) this.itemsContainer.innerHTML = '';
        if (this.emptyState) this.emptyState.style.display = 'flex';
        if (this.footerSection) this.footerSection.style.display = 'none';
      } else {
        if (this.emptyState) this.emptyState.style.display = 'none';
        if (this.footerSection) this.footerSection.style.display = 'block';

        // Render line items
        if (this.itemsContainer) {
          this.itemsContainer.innerHTML = cart.items.map(item => this.renderLineItemHTML(item)).join('');
        }

        // Update Subtotal
        if (this.subtotalElement) {
          const formattedSubtotal = this.formatMoney(cart.total_price);
          this.subtotalElement.innerHTML = formattedSubtotal;
        }

        // Apply discount link if existing
        if (this.appliedDiscount) {
          this.updateCheckoutUrlWithDiscount(this.appliedDiscount);
        }
      }
    }

    renderLineItemHTML(item) {
      const formattedLinePrice = this.formatMoney(item.final_line_price);
      const originalLinePrice = item.original_line_price > item.final_line_price
        ? `<s class="drawer-item__original-price">${this.formatMoney(item.original_line_price)}</s>`
        : '';

      const variantOptions = item.options_with_values && item.options_with_values.length > 0 && item.product_has_only_default_variant === false
        ? item.options_with_values.map(opt => `<span class="drawer-item__option-tag">${opt.name}: <strong>${opt.value}</strong></span>`).join(' ')
        : (item.variant_title && item.variant_title !== 'Default Title' ? `<span class="drawer-item__option-tag">${item.variant_title}</span>` : '');

      const imgUrl = item.image ? item.image : '//cdn.shopify.com/s/assets/no-image-2048-5eeceb1e10f98d1849e2b5738d3d737728d40730b359b09edd0711e63173d25b.gif';

      return `
        <div class="drawer-item" data-line-key="${item.key}">
          <a href="${item.url}" class="drawer-item__img-link">
            <img src="${imgUrl}" alt="${this.escapeHtml(item.title)}" width="80" height="100" class="drawer-item__img" loading="lazy">
          </a>
          <div class="drawer-item__info">
            <div class="drawer-item__header-row">
              <a href="${item.url}" class="drawer-item__title">${this.escapeHtml(item.product_title || item.title)}</a>
              <button type="button" class="drawer-item__remove-btn js-drawer-item-remove" data-line-key="${item.key}" aria-label="Remove item">
                ✕
              </button>
            </div>
            ${variantOptions ? `<div class="drawer-item__options">${variantOptions}</div>` : ''}
            <div class="drawer-item__bottom-row">
              <div class="drawer-item__qty-stepper">
                <button type="button" class="drawer-qty-btn js-drawer-qty-minus" data-line-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">-</button>
                <span class="drawer-qty-val">${item.quantity}</span>
                <button type="button" class="drawer-qty-btn js-drawer-qty-plus" data-line-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
              </div>
              <div class="drawer-item__price-wrap">
                ${originalLinePrice}
                <span class="drawer-item__final-price">${formattedLinePrice}</span>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    updateCheckoutUrlWithDiscount(code) {
      if (this.checkoutBtn) {
        this.checkoutBtn.href = `/checkout?discount=${encodeURIComponent(code)}`;
      }
    }

    formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      return 'PKR ' + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
  }

  // Initialize once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.ModairaCartInstance = new ModairaCart();
    });
  } else {
    window.ModairaCartInstance = new ModairaCart();
  }
})();
